import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Tag, Plus, Pencil, Trash2, X, Percent, IndianRupee } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminCoupons = () => {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({
    code: '', discount_type: 'percentage', discount_value: '', min_order: '', max_discount: '', expiry_date: '', max_usage: '', is_active: true
  });

  useEffect(() => { fetchCoupons(); }, []);

  const fetchCoupons = async () => {
    try {
      const res = await axios.get(`${API}/coupons/admin/all`, { withCredentials: true });
      setCoupons(res.data);
    } catch (err) {
      toast.error('Failed to load coupons');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setForm({ code: '', discount_type: 'percentage', discount_value: '', min_order: '', max_discount: '', expiry_date: '', max_usage: '', is_active: true });
    setEditingId(null);
    setShowForm(false);
  };

  const handleEdit = (coupon) => {
    setForm({
      code: coupon.code,
      discount_type: coupon.discount_type,
      discount_value: coupon.discount_value,
      min_order: coupon.min_order || '',
      max_discount: coupon.max_discount || '',
      expiry_date: coupon.expiry_date || '',
      max_usage: coupon.max_usage || '',
      is_active: coupon.is_active
    });
    setEditingId(coupon.id);
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.code || !form.discount_value) {
      toast.error('Code and discount value are required');
      return;
    }
    const payload = {
      ...form,
      discount_value: parseFloat(form.discount_value),
      min_order: parseFloat(form.min_order) || 0,
      max_discount: parseFloat(form.max_discount) || 0,
      max_usage: parseInt(form.max_usage) || 0,
      expiry_date: form.expiry_date || null
    };
    try {
      if (editingId) {
        await axios.put(`${API}/coupons/${editingId}`, payload, { withCredentials: true });
        toast.success('Coupon updated');
      } else {
        await axios.post(`${API}/coupons/admin/create`, payload, { withCredentials: true });
        toast.success('Coupon created');
      }
      resetForm();
      fetchCoupons();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save coupon');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this coupon?')) return;
    try {
      await axios.delete(`${API}/coupons/${id}`, { withCredentials: true });
      toast.success('Coupon deleted');
      fetchCoupons();
    } catch (err) {
      toast.error('Failed to delete coupon');
    }
  };

  const toggleActive = async (coupon) => {
    try {
      await axios.put(`${API}/coupons/${coupon.id}`, { is_active: !coupon.is_active }, { withCredentials: true });
      fetchCoupons();
    } catch (err) {
      toast.error('Failed to update');
    }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="spinner"></div></div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-heading text-2xl font-bold text-[#1A202C]" data-testid="admin-coupons-title">Coupons & Discounts</h2>
          <p className="text-sm text-[#718096]">{coupons.length} coupon{coupons.length !== 1 ? 's' : ''}</p>
        </div>
        <Button onClick={() => { resetForm(); setShowForm(true); }} className="btn-saffron" data-testid="create-coupon-btn">
          <Plus className="w-4 h-4 mr-2" /> New Coupon
        </Button>
      </div>

      {/* Form Modal */}
      {showForm && (
        <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading text-lg font-bold text-[#2D2D2D]">{editingId ? 'Edit Coupon' : 'Create Coupon'}</h3>
            <button onClick={resetForm}><X className="w-5 h-5 text-gray-400" /></button>
          </div>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label>Coupon Code *</Label>
                <Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} placeholder="DIWALI20" data-testid="coupon-code-input" />
              </div>
              <div>
                <Label>Discount Type</Label>
                <div className="flex mt-1 rounded-lg border border-gray-200 overflow-hidden">
                  <button type="button" onClick={() => setForm({ ...form, discount_type: 'percentage' })}
                    className={`flex-1 flex items-center justify-center gap-1 py-2 text-sm font-medium transition-colors ${form.discount_type === 'percentage' ? 'bg-[#FF9933] text-white' : 'bg-white text-gray-500'}`}
                    data-testid="discount-type-percentage"
                  >
                    <Percent className="w-3.5 h-3.5" /> Percentage
                  </button>
                  <button type="button" onClick={() => setForm({ ...form, discount_type: 'flat' })}
                    className={`flex-1 flex items-center justify-center gap-1 py-2 text-sm font-medium transition-colors ${form.discount_type === 'flat' ? 'bg-[#FF9933] text-white' : 'bg-white text-gray-500'}`}
                    data-testid="discount-type-flat"
                  >
                    <IndianRupee className="w-3.5 h-3.5" /> Flat
                  </button>
                </div>
              </div>
            </div>
            <div className="grid md:grid-cols-3 gap-4">
              <div>
                <Label>Discount Value *</Label>
                <Input type="number" value={form.discount_value} onChange={(e) => setForm({ ...form, discount_value: e.target.value })} placeholder={form.discount_type === 'percentage' ? '10' : '100'} data-testid="discount-value-input" />
              </div>
              <div>
                <Label>Min Order (₹)</Label>
                <Input type="number" value={form.min_order} onChange={(e) => setForm({ ...form, min_order: e.target.value })} placeholder="500" data-testid="min-order-input" />
              </div>
              <div>
                <Label>Max Discount (₹)</Label>
                <Input type="number" value={form.max_discount} onChange={(e) => setForm({ ...form, max_discount: e.target.value })} placeholder="200" data-testid="max-discount-input" />
              </div>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label>Expiry Date</Label>
                <Input type="date" value={form.expiry_date} onChange={(e) => setForm({ ...form, expiry_date: e.target.value })} data-testid="expiry-date-input" />
              </div>
              <div>
                <Label>Max Usage (0 = unlimited)</Label>
                <Input type="number" value={form.max_usage} onChange={(e) => setForm({ ...form, max_usage: e.target.value })} placeholder="100" data-testid="max-usage-input" />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <input type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} className="rounded border-gray-300" data-testid="coupon-active-checkbox" />
              <Label className="mb-0">Active</Label>
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <Button type="button" variant="outline" onClick={resetForm}>Cancel</Button>
              <Button type="submit" className="btn-saffron" data-testid="save-coupon-btn">
                {editingId ? 'Update Coupon' : 'Create Coupon'}
              </Button>
            </div>
          </form>
        </motion.div>
      )}

      {/* Coupons Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-[#FFF9F0] text-left">
                <th className="px-6 py-3 text-xs font-semibold text-[#4A5568] uppercase tracking-wider">Code</th>
                <th className="px-6 py-3 text-xs font-semibold text-[#4A5568] uppercase tracking-wider">Discount</th>
                <th className="px-6 py-3 text-xs font-semibold text-[#4A5568] uppercase tracking-wider">Min Order</th>
                <th className="px-6 py-3 text-xs font-semibold text-[#4A5568] uppercase tracking-wider">Usage</th>
                <th className="px-6 py-3 text-xs font-semibold text-[#4A5568] uppercase tracking-wider">Expiry</th>
                <th className="px-6 py-3 text-xs font-semibold text-[#4A5568] uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-xs font-semibold text-[#4A5568] uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {coupons.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-6 py-12 text-center text-[#718096]">
                    <Tag className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                    <p>No coupons yet. Create one!</p>
                  </td>
                </tr>
              ) : coupons.map((coupon) => (
                <tr key={coupon.id} className="hover:bg-gray-50 transition-colors" data-testid={`coupon-row-${coupon.code}`}>
                  <td className="px-6 py-4">
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-[#FF9933]/10 rounded-full text-sm font-bold text-[#FF6B00]">
                      <Tag className="w-3.5 h-3.5" /> {coupon.code}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm font-medium text-[#1A202C]">
                    {coupon.discount_type === 'percentage' ? `${coupon.discount_value}%` : `₹${coupon.discount_value}`}
                    {coupon.max_discount > 0 && coupon.discount_type === 'percentage' && (
                      <span className="text-xs text-[#718096] ml-1">(max ₹{coupon.max_discount})</span>
                    )}
                  </td>
                  <td className="px-6 py-4 text-sm text-[#4A5568]">
                    {coupon.min_order > 0 ? `₹${coupon.min_order}` : '-'}
                  </td>
                  <td className="px-6 py-4 text-sm text-[#4A5568]">
                    {coupon.usage_count || 0}{coupon.max_usage > 0 ? ` / ${coupon.max_usage}` : ' / ∞'}
                  </td>
                  <td className="px-6 py-4 text-sm text-[#4A5568]">
                    {coupon.expiry_date || 'No expiry'}
                  </td>
                  <td className="px-6 py-4">
                    <button
                      onClick={() => toggleActive(coupon)}
                      className={`px-3 py-1 rounded-full text-xs font-semibold transition-colors ${
                        coupon.is_active ? 'bg-green-100 text-green-700 hover:bg-green-200' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                      }`}
                      data-testid={`toggle-coupon-${coupon.code}`}
                    >
                      {coupon.is_active ? 'Active' : 'Inactive'}
                    </button>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <button onClick={() => handleEdit(coupon)} className="p-1.5 rounded-lg hover:bg-blue-50 text-blue-500" data-testid={`edit-coupon-${coupon.code}`}>
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button onClick={() => handleDelete(coupon.id)} className="p-1.5 rounded-lg hover:bg-red-50 text-red-500" data-testid={`delete-coupon-${coupon.code}`}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AdminCoupons;
